import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shiftLogApi, adminApi } from '../services/api';
import type { ShiftLogEntry } from '../services/api';
import type { Generator, Transformer } from '../types';
import toast from 'react-hot-toast';
import {
  adToBS, BS_MONTHS_FULL, todayAD,
  bsMonthRangeAD, bsDaysInMonth, bsToAD, fmtAD, getBSYearOptions,
  getCurrentBSYear, getCurrentBSMonth,
} from '../utils/bs';
import { NepaliDatePicker } from '../components/NepaliDatePicker';
import { exportMonthToExcel, parseExcelForImport } from '../utils/shiftLogExcel';
import type { ShiftLogPowerUnit, ShiftLogEnergyUnit } from '../utils/shiftLogExcel';

// ── Generator grouped columns ─────────────────────────────────────────────────

interface FieldDef { key: keyof ShiftLogEntry; label: string }
interface GroupDef { label: string; fields: FieldDef[] }

const GEN_GROUPS: GroupDef[] = [
  {
    label: 'Generator Output',
    fields: [
      { key: 'output_kw',     label: 'KW'   },
      { key: 'reactive_kvar', label: 'KVAR' },
    ],
  },
  {
    label: 'PF',
    fields: [
      { key: 'power_factor', label: 'CosØ' },
    ],
  },
  {
    label: 'Generating Voltage (kV)',
    fields: [
      { key: 'voltage_ry_kv', label: 'R-Y' },
      { key: 'voltage_yb_kv', label: 'Y-B' },
      { key: 'voltage_br_kv', label: 'B-R' },
    ],
  },
  {
    label: 'Freq.',
    fields: [
      { key: 'frequency_hz', label: 'Hz' },
    ],
  },
  {
    label: 'Generating Current (A)',
    fields: [
      { key: 'current_i1_a', label: 'I1' },
      { key: 'current_i2_a', label: 'I2' },
      { key: 'current_i3_a', label: 'I3' },
    ],
  },
  {
    label: 'Energy',
    fields: [
      { key: 'energy_kwh', label: 'KWH' },
    ],
  },
];

// Flat list of generator fields in order (for cell iteration)
const GEN_FIELDS: FieldDef[] = GEN_GROUPS.flatMap(g => g.fields);

// ── Transformer grouped columns ────────────────────────────────────────────────

const TRAFO_GROUPS: GroupDef[] = [
  {
    label: 'Transformer Output',
    fields: [
      // { key: 'output_mw',     label: 'MVA'  },
      { key: 'output_kw',     label: 'KW'   },
      { key: 'reactive_kvar', label: 'KVAR' },
    ],
  },
  {
    label: 'PF',
    fields: [
      { key: 'power_factor', label: 'CosØ' },
    ],
  },
  {
    label: 'HV Voltage (kV)',
    fields: [
      { key: 'voltage_ry_kv', label: 'R-Y' },
      { key: 'voltage_yb_kv', label: 'Y-B' },
      { key: 'voltage_br_kv', label: 'B-R' },
    ],
  },
  // {
  //   label: 'LV Voltage (kV)',
  //   fields: [
  //     { key: 'lv_voltage_ry_kv', label: 'R-Y' },
  //     { key: 'lv_voltage_yb_kv', label: 'Y-B' },
  //     { key: 'lv_voltage_br_kv', label: 'B-R' },
  //   ],
  // },
  {
    label: 'Freq.',
    fields: [
      { key: 'frequency_hz', label: 'Hz' },
    ],
  },
  {
    label: 'Current (A)',
    fields: [
      { key: 'current_i1_a', label: 'I1' },
      { key: 'current_i2_a', label: 'I2' },
      { key: 'current_i3_a', label: 'I3' },
    ],
  },
  {
    label: 'Energy',
    fields: [
      { key: 'energy_kwh', label: 'KWH' },
    ],
  },
];

const TRAFO_FIELDS: FieldDef[] = TRAFO_GROUPS.flatMap(g => g.fields);

const HOURS = Array.from({ length: 24 }, (_, i) => i);
function toNum(s: string | undefined): number | null {
  if (!s || s.trim() === '') return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}
function numAvg(vals: number[]) {
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
}

// ── Confirm modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ date, onConfirm, onCancel, isPending }: {
  date: string; onConfirm: () => void; onCancel: () => void; isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-5 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">Confirm Save</h2>
        </div>
        <div className="px-6 py-5 space-y-2">
          <p className="text-sm text-slate-700">
            Save shift log for{' '}
            <span className="font-semibold">{(() => { const d = new Date(date + 'T00:00:00'); const nd = adToBS(d); return `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}`; })()}</span>?
          </p>
          <p className="text-xs text-slate-400">Existing readings for this date will be overwritten.</p>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={onConfirm} disabled={isPending} className="btn-primary flex-1">
            {isPending ? 'Saving…' : 'Yes, Save'}
          </button>
          <button onClick={onCancel} disabled={isPending} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Export modal ──────────────────────────────────────────────────────────────

const YEAR_OPTIONS = getBSYearOptions(10);

function UnitToggle<T extends string>({ label, options, value, onChange, disabled }: {
  label: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1.5">{label}</p>
      <div className="flex gap-1.5">
        {options.map(opt => (
          <button key={opt} type="button" disabled={disabled} onClick={() => onChange(opt)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              value === opt
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-slate-600 border-hydro-border hover:border-brand-400 hover:text-brand-700'
            }`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function ExportModal({
  generators, transformers, plantName, onClose,
}: {
  generators: Generator[];
  transformers: Transformer[];
  plantName: string;
  onClose: () => void;
}) {
  const [bsYear,     setBsYear]     = useState(getCurrentBSYear());
  const [bsMonth,    setBsMonth]    = useState(getCurrentBSMonth());
  const [powerUnit,  setPowerUnit]  = useState<ShiftLogPowerUnit>('kW');
  const [energyUnit, setEnergyUnit] = useState<ShiftLogEnergyUnit>('kWh');
  const [busy,       setBusy]       = useState(false);
  const [progress,   setProgress]   = useState('');

  async function handleExport() {
    setBusy(true);
    try {
      const days = bsDaysInMonth(bsYear, bsMonth);
      const byDate: Record<string, ShiftLogEntry[]> = {};

      for (let day = 1; day <= days; day++) {
        setProgress(`Fetching day ${day}/${days}…`);
        const adDate = bsToAD(bsYear, bsMonth, day);
        const dateStr = fmtAD(adDate);
        const { data } = await shiftLogApi.get(dateStr);
        if (data.hasData) byDate[dateStr] = data.logs;
      }

      setProgress('Building file…');
      await exportMonthToExcel(bsYear, bsMonth, byDate, generators, transformers, plantName || undefined, powerUnit, energyUnit);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed — check console for details');
    } finally {
      setBusy(false);
      setProgress('');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-5 border-b border-hydro-border flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Export Shift Log</h2>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-700 text-lg leading-none">✕</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {plantName && (
            <p className="text-xs font-medium text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-hydro-border">
              {plantName}
            </p>
          )}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">BS Year</label>
            <select value={bsYear} onChange={e => setBsYear(Number(e.target.value))} className="input w-full" disabled={busy}>
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">BS Month</label>
            <select value={bsMonth} onChange={e => setBsMonth(Number(e.target.value))} className="input w-full" disabled={busy}>
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <UnitToggle label="Power Unit" options={['kW', 'MW'] as ShiftLogPowerUnit[]} value={powerUnit} onChange={setPowerUnit} disabled={busy} />
          <UnitToggle label="Energy Unit" options={['kWh', 'MWh', 'GWh'] as ShiftLogEnergyUnit[]} value={energyUnit} onChange={setEnergyUnit} disabled={busy} />
          {progress ? (
            <p className="text-xs text-brand-600 font-medium animate-pulse">{progress}</p>
          ) : (
            <p className="text-xs text-slate-400">
              Downloads all {bsDaysInMonth(bsYear, bsMonth)} days of {BS_MONTHS_FULL[bsMonth]} {bsYear} as a
              single Excel file. Power in {powerUnit}, energy in {energyUnit}.
            </p>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={handleExport} disabled={busy} className="btn-primary flex-1">
            {busy ? 'Exporting…' : 'Export Excel'}
          </button>
          <button onClick={onClose} disabled={busy} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Import modal ──────────────────────────────────────────────────────────────

function ImportModal({
  generators, transformers, onClose, onDone,
}: {
  generators: Generator[];
  transformers: Transformer[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [bsYear,     setBsYear]     = useState(getCurrentBSYear());
  const [bsMonth,    setBsMonth]    = useState(getCurrentBSMonth());
  const [powerUnit,  setPowerUnit]  = useState<ShiftLogPowerUnit>('kW');
  const [energyUnit, setEnergyUnit] = useState<ShiftLogEnergyUnit>('kWh');
  const [file,     setFile]     = useState<File | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [result,   setResult]   = useState<{ saved: number; failed: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleImport() {
    if (!file) { toast.error('Select a file first'); return; }
    if (generators.length === 0 && transformers.length === 0) {
      toast.error('Add at least one generator or transformer to this plant before importing');
      return;
    }
    setProgress('Parsing file…');
    try {
      const dayData = await parseExcelForImport(file, generators, transformers, bsYear, bsMonth, powerUnit, energyUnit);
      if (dayData.length === 0) { toast.error('No valid day sheets found'); setProgress(null); return; }

      let saved = 0; let failed = 0;
      for (let i = 0; i < dayData.length; i++) {
        setProgress(`Saving day ${i + 1} of ${dayData.length}…`);
        try {
          await shiftLogApi.bulkSave(dayData[i].date, dayData[i].entries);
          saved++;
        } catch { failed++; }
      }

      setResult({ saved, failed });
      setProgress(null);
      onDone();
    } catch {
      toast.error('Failed to parse the Excel file');
      setProgress(null);
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
          <div className="px-6 py-10 text-center space-y-3">
            <div className="text-3xl">✓</div>
            <p className="text-base font-bold text-slate-900">Import Complete</p>
            <p className="text-sm text-slate-500">
              {result.saved} day{result.saved !== 1 ? 's' : ''} saved
              {result.failed > 0 && `, ${result.failed} failed`}
            </p>
            <button onClick={onClose} className="btn-primary mt-2">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-5 border-b border-hydro-border flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Import Shift Log</h2>
          <button onClick={onClose} disabled={!!progress} className="text-slate-400 hover:text-slate-700 text-lg leading-none">✕</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">BS Year</label>
            <select value={bsYear} onChange={e => setBsYear(Number(e.target.value))} className="input w-full" disabled={!!progress}>
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">BS Month</label>
            <select value={bsMonth} onChange={e => setBsMonth(Number(e.target.value))} className="input w-full" disabled={!!progress}>
              {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <UnitToggle label="Power Unit (file)" options={['kW', 'MW'] as ShiftLogPowerUnit[]} value={powerUnit} onChange={setPowerUnit} disabled={!!progress} />
          <UnitToggle label="Energy Unit (file)" options={['kWh', 'MWh', 'GWh'] as ShiftLogEnergyUnit[]} value={energyUnit} onChange={setEnergyUnit} disabled={!!progress} />
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Excel File (.xlsx)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              disabled={!!progress}
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-500
                file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0
                file:text-xs file:font-medium file:bg-brand-50 file:text-brand-700
                hover:file:bg-brand-100 cursor-pointer"
            />
          </div>
          {progress ? (
            <p className="text-xs text-brand-600 font-medium animate-pulse">{progress}</p>
          ) : (
            <p className="text-xs text-slate-400">
              Select the units used in the file. Each sheet must be named by day (01, 02 …).
            </p>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={handleImport} disabled={!file || !!progress} className="btn-primary flex-1">
            {progress ? 'Importing…' : 'Import'}
          </button>
          <button onClick={onClose} disabled={!!progress} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ShiftLog() {
  const qc    = useQueryClient();
  const [date, setDate] = useState(todayAD());
  const [equipType,   setEquipType]   = useState<'generator' | 'transformer'>('generator');
  const [equipId,     setEquipId]     = useState<number | null>(null);
  const [editMode,    setEditMode]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showExport,  setShowExport]  = useState(false);
  const [showImport,  setShowImport]  = useState(false);
  const [cells,       setCells]       = useState<Record<number, Record<string, string>>>(
    () => Object.fromEntries(HOURS.map(h => [h, {}]))
  );

  // ── Equipment ──────────────────────────────────────────────────────────────
  const { data: plantData } = useQuery({
    queryKey: ['admin-plant'],
    queryFn:  () => adminApi.plant().then(r => r.data.plant),
    staleTime: Infinity,
  });
  const plantName = plantData?.name ?? '';

  const { data: generators = [], isLoading: genLoading } = useQuery<Generator[]>({
    queryKey: ['admin-generators'],
    queryFn:  () => adminApi.generators().then(r => r.data.generators),
  });
  const { data: transformers = [], isLoading: trafoLoading } = useQuery<Transformer[]>({
    queryKey: ['admin-transformers'],
    queryFn:  () => adminApi.transformers().then(r => r.data.transformers),
  });

  const equipList: Array<{ id: number; name: string }> =
    equipType === 'generator' ? generators : transformers;

  const activeFields = equipType === 'generator' ? GEN_FIELDS : TRAFO_FIELDS;
  const activePrefix = equipType === 'generator' ? 'g' : 't';

  useEffect(() => {
    if (equipList.length > 0) {
      setEquipId(prev => {
        const stillValid = equipList.some(e => e.id === prev);
        return stillValid ? prev : equipList[0].id;
      });
    }
  }, [equipType, equipList.length]);

  const selectedEquip = equipList.find(e => e.id === equipId) ?? null;

  // ── Fetch log ──────────────────────────────────────────────────────────────
  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ['shift-log', date],
    queryFn:  () => shiftLogApi.get(date).then(r => r.data),
  });

  const hasData   = logData?.hasData ?? false;
  const isEditing = editMode || (!hasData && !logLoading);

  useEffect(() => {
    const init: Record<number, Record<string, string>> =
      Object.fromEntries(HOURS.map(h => [h, {}]));

    if (logData?.logs) {
      for (const row of logData.logs) {
        const prefix = row.equipment_type === 'generator' ? 'g' : 't';
        const fields = row.equipment_type === 'generator' ? GEN_FIELDS : TRAFO_FIELDS;
        fields.forEach(f => {
          const v = (row as unknown as Record<string, unknown>)[String(f.key)];
          if (v != null) {
            if (!init[row.hour]) init[row.hour] = {};
            init[row.hour][`${prefix}_${row.equipment_id}_${String(f.key)}`] = String(v);
          }
        });
      }
    }
    setCells(init);
    setEditMode(false);
  }, [logData, date]);

  // ── Cell helpers ───────────────────────────────────────────────────────────
  function ck(hour: number, fieldKey: string) {
    return `${activePrefix}_${equipId}_${fieldKey}`;
  }
  function getCell(hour: number, fieldKey: string) {
    return cells[hour]?.[ck(hour, fieldKey)] ?? '';
  }
  function setCell(hour: number, fieldKey: string, val: string) {
    const n = toNum(val);
    if (n !== null && n < 0) {
      toast.error('Negative readings are not allowed');
      return;
    }
    const key = ck(hour, fieldKey);
    setCells(prev => ({ ...prev, [hour]: { ...(prev[hour] ?? {}), [key]: val } }));
  }

  // ── Overview summary ───────────────────────────────────────────────────────
  const overviewRows = useMemo(() => {
    type Row = { id: number; name: string; type: string; hours: number; totalKwOrMw: number; totalKwh: number };
    const rows: Row[] = [];

    generators.forEach(gen => {
      let hrs = 0; let totalKw = 0; let totalKwh = 0;
      HOURS.forEach(h => {
        const row = cells[h] ?? {};
        const kw  = toNum(row[`g_${gen.id}_output_kw`]);
        const kwh = toNum(row[`g_${gen.id}_energy_kwh`]);
        if (kw != null) { hrs++; totalKw += kw; }
        if (kwh != null) totalKwh += kwh;
      });
      rows.push({ id: gen.id, name: gen.name, type: 'Generator', hours: hrs, totalKwOrMw: totalKw, totalKwh });
    });

    transformers.forEach(t => {
      let hrs = 0; let totalKw = 0; let totalKwh = 0;
      HOURS.forEach(h => {
        const row = cells[h] ?? {};
        const kw  = toNum(row[`t_${t.id}_output_kw`]);
        const kwh = toNum(row[`t_${t.id}_energy_kwh`]);
        if (kw != null) { hrs++; totalKw += kw; }
        if (kwh != null) totalKwh += kwh;
      });
      rows.push({ id: t.id, name: t.name, type: 'Transformer', hours: hrs, totalKwOrMw: totalKw, totalKwh });
    });

    return rows;
  }, [cells, generators, transformers]);

  const totalKwh       = overviewRows.reduce((s, r) => s + r.totalKwh, 0);
  const anyOverviewData = overviewRows.some(r => r.hours > 0);

  // ── Build all entries for save ─────────────────────────────────────────────
  function buildEntries(): ShiftLogEntry[] {
    const entries: ShiftLogEntry[] = [];
    HOURS.forEach(h => {
      const row = cells[h] ?? {};
      generators.forEach(gen => {
        entries.push({
          hour: h, equipment_type: 'generator', equipment_id: gen.id,
          output_kw:      toNum(row[`g_${gen.id}_output_kw`]),
          reactive_kvar:  toNum(row[`g_${gen.id}_reactive_kvar`]),
          power_factor:   toNum(row[`g_${gen.id}_power_factor`]),
          voltage_ry_kv:  toNum(row[`g_${gen.id}_voltage_ry_kv`]),
          voltage_yb_kv:  toNum(row[`g_${gen.id}_voltage_yb_kv`]),
          voltage_br_kv:  toNum(row[`g_${gen.id}_voltage_br_kv`]),
          frequency_hz:   toNum(row[`g_${gen.id}_frequency_hz`]),
          current_i1_a:   toNum(row[`g_${gen.id}_current_i1_a`]),
          current_i2_a:   toNum(row[`g_${gen.id}_current_i2_a`]),
          current_i3_a:   toNum(row[`g_${gen.id}_current_i3_a`]),
          energy_kwh:     toNum(row[`g_${gen.id}_energy_kwh`]),
        });
      });
      transformers.forEach(t => {
        entries.push({
          hour: h, equipment_type: 'transformer', equipment_id: t.id,
          output_mw:        toNum(row[`t_${t.id}_output_mw`]),
          output_kw:        toNum(row[`t_${t.id}_output_kw`]),
          reactive_kvar:    toNum(row[`t_${t.id}_reactive_kvar`]),
          power_factor:     toNum(row[`t_${t.id}_power_factor`]),
          voltage_ry_kv:    toNum(row[`t_${t.id}_voltage_ry_kv`]),
          voltage_yb_kv:    toNum(row[`t_${t.id}_voltage_yb_kv`]),
          voltage_br_kv:    toNum(row[`t_${t.id}_voltage_br_kv`]),
          lv_voltage_ry_kv: toNum(row[`t_${t.id}_lv_voltage_ry_kv`]),
          lv_voltage_yb_kv: toNum(row[`t_${t.id}_lv_voltage_yb_kv`]),
          lv_voltage_br_kv: toNum(row[`t_${t.id}_lv_voltage_br_kv`]),
          frequency_hz:     toNum(row[`t_${t.id}_frequency_hz`]),
          current_i1_a:     toNum(row[`t_${t.id}_current_i1_a`]),
          current_i2_a:     toNum(row[`t_${t.id}_current_i2_a`]),
          current_i3_a:     toNum(row[`t_${t.id}_current_i3_a`]),
          energy_kwh:       toNum(row[`t_${t.id}_energy_kwh`]),
        });
      });
    });
    return entries;
  }

  const saveMut = useMutation({
    mutationFn: () => shiftLogApi.bulkSave(date, buildEntries()),
    onSuccess: () => {
      toast.success('Shift log saved');
      qc.invalidateQueries({ queryKey: ['shift-log', date] });
      setShowConfirm(false);
      setEditMode(false);
    },
    onError: () => toast.error('Save failed'),
  });

  const equipLoading = genLoading || trafoLoading;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Daily Shift Log</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Record hourly readings per equipment. Select a date and equipment to get started.
        </p>
      </div>

      {/* ── Controls ── */}
      <div className="card flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Date (BS)</label>
          <NepaliDatePicker value={date} onChange={v => { setDate(v); setEditMode(false); }} className="w-48" maxDate={todayAD()} />
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Equipment Type</label>
          <div className="flex border border-hydro-border rounded-lg overflow-hidden">
            {(['generator', 'transformer'] as const).map(t => (
              <button key={t} onClick={() => setEquipType(t)}
                className={`px-4 py-2 text-xs font-medium capitalize transition-colors ${
                  equipType === t ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                {t === 'generator' ? 'Generator' : 'Transformer'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Equipment</label>
          {equipLoading ? (
            <div className="input w-44 text-xs text-slate-400">Loading…</div>
          ) : (
            <select className="input w-44 text-sm" value={equipId ?? ''}
              onChange={e => setEquipId(Number(e.target.value))}>
              {equipList.length === 0 && <option value="">No equipment</option>}
              {equipList.map(eq => <option key={eq.id} value={eq.id}>{eq.name}</option>)}
            </select>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3">
          {hasData && !isEditing && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200">
              ✓ Saved
            </span>
          )}
          {!logLoading && hasData && !isEditing && (
            <button onClick={() => setEditMode(true)} className="btn-ghost text-sm">Edit</button>
          )}
          {!logLoading && isEditing && hasData && (
            <button onClick={() => setEditMode(false)} className="btn-ghost text-sm">Cancel</button>
          )}
          {!logLoading && isEditing && (
            <button onClick={() => setShowConfirm(true)} disabled={saveMut.isPending} className="btn-primary">
              Save Data
            </button>
          )}
          <div className="h-6 w-px bg-slate-200" />
          <button onClick={() => setShowImport(true)} className="btn-ghost text-sm">Import</button>
          <button onClick={() => setShowExport(true)} className="btn-ghost text-sm">Export</button>
        </div>
      </div>

      {logLoading ? (
        <div className="card text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          {/* ── Overview table ── */}
          {anyOverviewData && (
            <div className="card p-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-hydro-border">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Daily Overview — {(() => { const d = new Date(date + 'T00:00:00'); const nd = adToBS(d); return `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}`; })()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider bg-slate-50">
                      {['Equipment', 'Type', 'Hrs Recorded', 'Total Output', 'Total Energy (kWh)'].map(h => (
                        <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overviewRows.map(row => (
                      <tr key={`${row.type}-${row.id}`}
                        onClick={() => { setEquipType(row.type.toLowerCase() as 'generator' | 'transformer'); setEquipId(row.id); }}
                        className={`table-row text-xs border-b border-hydro-border last:border-0 cursor-pointer transition-colors ${
                          row.id === equipId && row.type.toLowerCase() === equipType ? 'bg-brand-50/60' : 'hover:bg-slate-50'
                        }`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">{row.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.type === 'Generator' ? 'bg-brand-100 text-brand-700' : 'bg-amber-100 text-amber-700'
                          }`}>{row.type}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-slate-700">{row.hours}</td>
                        <td className="px-4 py-3 font-mono text-blue-600">
                          {row.totalKwOrMw > 0 ? `${row.totalKwOrMw.toFixed(2)} KW` : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-emerald-700">
                          {row.totalKwh > 0 ? row.totalKwh.toFixed(2) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-hydro-border bg-slate-50 text-xs font-bold">
                      <td colSpan={4} className="px-4 py-3 text-slate-700 uppercase tracking-wide">Total Energy</td>
                      <td className="px-4 py-3 font-mono text-emerald-700">{totalKwh.toFixed(2)} kWh</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Hourly entry / view table ── */}
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-hydro-border flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Hourly Readings{selectedEquip ? ` — ${selectedEquip.name}` : ''}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {(() => { const d = new Date(date + 'T00:00:00'); const nd = adToBS(d); return `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}`; })()}
                </p>
              </div>
              {isEditing && selectedEquip && (
                <span className="text-xs text-brand-600 font-medium bg-brand-50 px-2 py-1 rounded-full border border-brand-200">
                  Editing
                </span>
              )}
            </div>

            {equipLoading ? (
              <div className="px-4 py-10 text-center text-slate-400 text-sm">Loading equipment…</div>
            ) : equipList.length === 0 ? (
              <div className="px-4 py-10 text-center text-slate-400 text-sm">
                No {equipType}s configured for this plant.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    {(() => {
                      const groups = equipType === 'generator' ? GEN_GROUPS : TRAFO_GROUPS;
                      const fields = equipType === 'generator' ? GEN_FIELDS : TRAFO_FIELDS;
                      return (
                        <>
                          <tr className="border-b border-hydro-border bg-slate-50 text-xs font-semibold text-slate-700">
                            <th rowSpan={2} className="px-4 py-2.5 text-left border-r border-hydro-border align-middle w-16">
                              Time
                            </th>
                            {groups.map(g => (
                              <th
                                key={g.label}
                                colSpan={g.fields.length}
                                className="px-3 py-2 text-center border-b border-hydro-border border-l border-slate-200 whitespace-nowrap"
                              >
                                {g.label}
                              </th>
                            ))}
                          </tr>
                          <tr className="border-b border-hydro-border bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                            {fields.map(f => (
                              <th key={String(f.key)} className="px-3 py-2 text-center font-medium border-l border-slate-200 whitespace-nowrap">
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </>
                      );
                    })()}
                  </thead>

                  <tbody>
                    {HOURS.map(h => (
                      <tr key={h} className={`table-row border-b border-hydro-border last:border-0 ${h % 2 !== 0 ? 'bg-slate-50/40' : ''}`}>
                        <td className="px-4 py-1.5 font-mono font-semibold text-xs text-slate-700 whitespace-nowrap border-r border-hydro-border">
                          {h + 1}
                        </td>
                        {activeFields.map(f => {
                          const fk  = String(f.key);
                          const val = getCell(h, fk);
                          return isEditing ? (
                            <td key={fk} className="px-1 py-1">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                className="input w-full text-xs font-mono py-1 text-right min-w-[60px]"
                                value={val}
                                onChange={e => setCell(h, fk, e.target.value)}
                                placeholder="—"
                              />
                            </td>
                          ) : (
                            <td key={fk} className="px-3 py-2 text-xs font-mono text-slate-700 text-right">
                              {val !== '' ? val : <span className="text-slate-300">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Sticky save */}
      {isEditing && !logLoading && (
        <div className="sticky bottom-4 flex justify-end pointer-events-none">
          <button onClick={() => setShowConfirm(true)} disabled={saveMut.isPending}
            className="btn-primary shadow-xl px-8 pointer-events-auto">
            Save Data
          </button>
        </div>
      )}

      {showConfirm && (
        <ConfirmModal
          date={date}
          onConfirm={() => saveMut.mutate()}
          onCancel={() => setShowConfirm(false)}
          isPending={saveMut.isPending}
        />
      )}

      {showExport && (
        <ExportModal
          generators={generators}
          transformers={transformers}
          plantName={plantName}
          onClose={() => setShowExport(false)}
        />
      )}

      {showImport && (
        <ImportModal
          generators={generators}
          transformers={transformers}
          onClose={() => setShowImport(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['shift-log'] })}
        />
      )}
    </div>
  );
}
